import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
const EASE = [0.23, 1, 0.32, 1] as const;
type Bloom = {
  id: number;
  x: number;
  y: number;
};

/**
 * The shared "you pressed it" moment for Talab's AI buttons.
 *
 * - A bloom of light opens from the exact point you pressed and washes
 *   across the fill (white core, purple edge), then fades.
 * - A ring of indigo→purple light leaves the button's outline and expands
 *   outward, so the confirmation is visible even at a glance.
 *
 * Use `trigger(e)` in the click handler, and render `layers` inside the
 * button (it must be `relative isolate`, and the fill clip `overflow-hidden`).
 */
export function usePressBloom(radiusClass = 'rounded-lg') {
  const reduce = useReducedMotion();
  const [blooms, setBlooms] = useState<Bloom[]>([]);
  const trigger = useCallback((e: React.MouseEvent<HTMLElement>) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    // Keyboard clicks report 0,0 — bloom from the centre instead.
    const fromKeyboard = e.clientX === 0 && e.clientY === 0;
    const b = {
      id: Date.now() + Math.random(),
      x: fromKeyboard ? r.width / 2 : e.clientX - r.left,
      y: fromKeyboard ? r.height / 2 : e.clientY - r.top
    };
    setBlooms(list => [...list, b]);
    window.setTimeout(() => setBlooms(list => list.filter(x => x.id !== b.id)), 650);
  }, [reduce]);

  /** Inside the clipped fill. */
  const fillLayer = <AnimatePresence data-id="element-3167" data-mp="939o9cn8qugr">
      {blooms.map(b => <motion.span key={b.id} aria-hidden className="pointer-events-none absolute w-10 h-10 -ml-5 -mt-5 rounded-full" style={{
      left: b.x,
      top: b.y,
      background: 'radial-gradient(circle, rgba(255,255,255,0.55) 0%, rgba(196,181,253,0.45) 35%, rgba(168,85,247,0) 70%)'
    }} initial={{
      scale: 0.4,
      opacity: 1
    }} animate={{
      scale: 9,
      opacity: 0
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.55,
      ease: EASE
    }} data-id="element-3168" data-mp="e005426vdu38" />)}
    </AnimatePresence>;

  /** Outside the clip, around the button's outline. */
  const ringLayer = <AnimatePresence data-id="element-3169" data-mp="2hp4mnva52ixd">
      {blooms.map(b => <motion.span key={`ring-${b.id}`} aria-hidden className={`pointer-events-none absolute -inset-px ${radiusClass} ring-2 ring-ai-purple/70 shadow-[0_0_18px_2px_rgba(139,92,246,0.35)]`} initial={{
      opacity: 1,
      scale: 1
    }} animate={{
      opacity: 0,
      scale: 1.14
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.45,
      ease: EASE
    }} data-id="element-3170" data-mp="2hnjozwsj2jva" />)}
    </AnimatePresence>;
  return {
    trigger,
    fillLayer,
    ringLayer,
    active: blooms.length > 0
  };
}